import React, { useState } from 'react';
import {
    Card,
    Button,
    Alert,
    Form,
    Modal,
    Row,
    Col
} from 'react-bootstrap';
import { getAuthenticatedAxios } from './axiosConfig';
import { useAuth } from '../AuthContext/AuthContext';
import { BASE_URL } from '../../Constants/constants';
import { useTranslation } from 'react-i18next';

const HRCheckoutStage = ({ checkoutStatus, onCheckoutUpdate }) => {
    const { t } = useTranslation('hrCheckoutStage');
    const { currentUser } = useAuth();
    const [exitInterviewCompleted, setExitInterviewCompleted] = useState(false);
    const [additionalNotes, setAdditionalNotes] = useState('');
    const [error, setError] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    // Optional checkout requirements - use translations
    const getCheckoutRequirements = () => [
        { id: 'clearance', label: t('requirements.clearance'), checked: false },
        { id: 'documents', label: t('requirements.documents'), checked: false },
        { id: 'interview', label: t('requirements.interview'), checked: false },
        { id: 'finances', label: t('requirements.finances'), checked: false }
    ];

    // State for tracking individual requirements
    const [requirements, setRequirements] = useState(getCheckoutRequirements());

    // Update individual requirement status
    const updateRequirement = (id) => {
        const updatedRequirements = requirements.map(req =>
            req.id === id ? { ...req, checked: !req.checked } : req
        );
        setRequirements(updatedRequirements);
    };

    // Check if any requirements are completed (for informational purposes)
    const getCompletedRequirementsCount = () => {
        const completedChecklistItems = requirements.filter(req => req.checked).length;
        const exitInterviewCount = exitInterviewCompleted ? 1 : 0;
        return completedChecklistItems + exitInterviewCount;
    };

    const getTotalRequirementsCount = () => {
        return requirements.length + 1; // +1 for exit interview
    };

    // Handle HR checkout finalization
    const handleHRCheckout = async () => {
        try {
            setIsProcessing(true);

            const checkoutData = {
                user_id: currentUser.id,
                notes: additionalNotes || 'Checkout process completed',
                checklist: requirements.map(req => ({
                    id: req.id,
                    label: req.label,
                    status: req.checked
                })),
                exit_interview_completed: exitInterviewCompleted
            };

            const authenticatedAxios = getAuthenticatedAxios();
            const response = await authenticatedAxios.post(
                `${BASE_URL}/checkout/hr-finalize`,
                checkoutData
            );

            onCheckoutUpdate(response.data);
            setError(null);
            setShowConfirmModal(false);

        } catch (err) {
            console.error('HR Checkout Error:', err);
            setError(
                err.response?.data?.detail ||
                'Failed to complete HR checkout process'
            );
        } finally {
            setIsProcessing(false);
        }
    };

    const completedCount = getCompletedRequirementsCount();
    const totalCount = getTotalRequirementsCount();

    return (
        <Card>
            <Card.Header>
                <h3>{t('heading')}</h3>
            </Card.Header>
            <Card.Body>
                {/* Error Notification */}
                {error && (
                    <Alert
                        variant="danger"
                        onClose={() => setError(null)}
                        dismissible
                    >
                        {error}
                    </Alert>
                )}

                {/* Checkout Process Instructions */}
                <Alert variant="info">
                    <strong>{t('final_stage')}</strong>
                    <p className="mb-2">{t('instructions')}</p>
                    <p className="mb-0">
                        <strong>{t('progress', { completed: completedCount, total: totalCount })}</strong>
                    </p>
                </Alert>

                {/* Optional Checkout Requirements Checklist */}
                <Card className="mb-3">
                    <Card.Header>{t('requirements.title')}</Card.Header>
                    <Card.Body>
                        <p className="text-muted mb-3">
                            {t('requirements.description')}
                        </p>
                        {requirements.map(req => (
                            <Form.Group key={req.id} className="mb-2">
                                <Form.Check
                                    type="checkbox"
                                    id={`requirement-${req.id}`}
                                    label={req.label}
                                    checked={req.checked}
                                    onChange={() => updateRequirement(req.id)}
                                />
                            </Form.Group>
                        ))}
                    </Card.Body>
                </Card>

                {/* Exit Interview Section */}
                <Card className="mb-3">
                    <Card.Header>{t('exit_interview.title')}</Card.Header>
                    <Card.Body>
                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                id="exit-interview-completed"
                                label={t('exit_interview.completed')}
                                checked={exitInterviewCompleted}
                                onChange={() => setExitInterviewCompleted(!exitInterviewCompleted)}
                            />
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>{t('exit_interview.notes_label')}</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={4}
                                placeholder={t('exit_interview.notes_placeholder')}
                                value={additionalNotes}
                                onChange={(e) => setAdditionalNotes(e.target.value)}
                            />
                        </Form.Group>
                    </Card.Body>
                </Card>

                {/* Checkout Finalization Button */}
                <div className="text-center">
                    <Button
                        variant="success"
                        size="lg"
                        onClick={() => setShowConfirmModal(true)}
                    >
                        {t('buttons.finalize')}
                    </Button>
                    {completedCount === 0 && (
                        <p className="text-muted mt-2 mb-0">
                            {t('proceed_message')}
                        </p>
                    )}
                </div>

                {/* Confirmation Modal */}
                <Modal
                    show={showConfirmModal}
                    onHide={() => setShowConfirmModal(false)}
                    centered
                >
                    <Modal.Header closeButton>
                        <Modal.Title>{t('confirmation.title')}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Alert variant="info">
                            <strong>{t('confirmation.summary')}</strong>
                            <ul className="mb-2">
                                <li>{t('confirmation.completed_items', { completed: completedCount, total: totalCount })}</li>
                                <li>
                                    {t('confirmation.exit_interview_status')}: {' '}
                                    {exitInterviewCompleted 
                                        ? t('confirmation.exit_interview_completed') 
                                        : t('confirmation.exit_interview_not_completed')}
                                </li>
                                <li>
                                    {t('confirmation.notes')}: {' '}
                                    {additionalNotes 
                                        ? t('confirmation.notes_provided') 
                                        : t('confirmation.notes_none')}
                                </li>
                            </ul>
                        </Alert>
                        <p>{t('confirmation.message')}</p>
                        {completedCount < totalCount && (
                            <Alert variant="warning" className="mb-0">
                                <small>{t('confirmation.incomplete_warning')}</small>
                            </Alert>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            variant="secondary"
                            onClick={() => setShowConfirmModal(false)}
                        >
                            {t('buttons.cancel')}
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleHRCheckout}
                            disabled={isProcessing}
                        >
                            {isProcessing ? t('buttons.processing') : t('buttons.confirm')}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </Card.Body>
        </Card>
    );
};

export default HRCheckoutStage;
